<script>
export default {
	name: 'PassengerAndCabinClass',
};
</script>

<script setup>
import { unref } from 'vue';
import Dropdown from '~/components/form/Dropdown.vue';
import { isMobileViewport } from '~/logic/composables/breakpoints.js';
import NumberPlusMinus from '~/components/form/NumberPlusMinus.vue';
import CTAButton from '~/aem-components/CTAButton.vue';
import { usePassengers } from '~composables/booking-widget/usePassengers.js';
import TextField from '~/components/form/TextField.vue';
import { i18nGlobal } from '~/logic/i18n.js';
import { useEventBus } from '@vueuse/core';
import { useRoute } from 'vue-router';

const props = defineProps({
	passengerAdultsName: { type: String, default: 'passengerAdults' },
	passengerTeensName: { type: String, default: 'passengerTeens' },
	passengerChildrenName: { type: String, default: 'passengerChildren' },
	passengerInfantsName: { type: String, default: 'passengerInfants' },
	cabinClassName: { type: String, default: 'cabinClass' },
	
	labelText: { type: String, default: i18nGlobal.t('Passengers and cabin class') },
	isUKVariant: { type: Boolean, default: false },
	isCountryVariant: { type: Boolean, default: false },
	defaultValues: { type: Object, default: () => ({}) },
	
	isShowEconomyClass: { type: Boolean, default: true },
	isShowBusinessClass: { type: Boolean, default: true },
	isShowBusinessSuiteClass: { type: Boolean, default: true },

	isShowChildren: { type: Boolean, default: true },

	passengerLabel: { type: String, default: i18nGlobal.t('Passengers') },
	adultLabel: { type: String, default: i18nGlobal.t('Adults') },
	teenagerLabel: { type: String, default: i18nGlobal.t('Teenagers') },
	childrenLabel: { type: String, default: i18nGlobal.t('Children') },
	infantLabel: { type: String, default: i18nGlobal.t('Infants') },
	cabinClassLabel: { type: String, default: i18nGlobal.t('Cabin Class') },
	economyLabel: { type: String, default: i18nGlobal.t('Economy') },
	businessLabel: { type: String, default: i18nGlobal.t('Business') },
	businessSuiteLabel: { type: String, default: i18nGlobal.t('Business Suite') },
	maxInfantsCount: { type: Number, default: null },
	isExpressBooking: { type: Boolean, default: false },
	hideExpressBookingNote: { type: Boolean, default: false },
	passengerAndCabinClassNote: { type: String, default: '' },
	isDisabled: { type: Boolean, default: false },
	enableGlassDesign: { type: Boolean, default: false },
});
const emit = defineEmits([
	// 'update:modelValue',
]);

const dropdownEl = ref(null);

const route = useRoute();

const computedCabinClassOptions = computed(() => {
	return [
		...(props.isShowEconomyClass ? [{
			label: props.economyLabel,
			value: '1',
			icon: new URL('@/assets/seat-eco.svg', import.meta.url).href,
		}] : []),
		...(props.isShowBusinessClass ? [{
			label: props.businessLabel,
			value: '2',
			icon: new URL('@/assets/seat-biz.svg', import.meta.url).href,
		}] : []),
		...(props.isShowBusinessSuiteClass ? [{
			label: props.businessSuiteLabel,
			value: '3',
			icon: new URL('@/assets/seat-bs.svg', import.meta.url).href,
		}] : []),
	];
});

const cabinClassValue = ref(props.defaultValues.cabinClassValue ?? computedCabinClassOptions.value[0] ?? {});

const handleSelectCabinClass = (item) => {
	cabinClassValue.value = item;
};

const {
	adults,
	teens,
	children,
	infants,
	totalPassengers,
	totalPassengersIncludedInfantCount,
	hasReachedMaxPassengersCount,
	showInfantErrorMsg,
} = usePassengers({
	maxPassengers: 9,
	
	...(props.maxInfantsCount ?
		{ maxInfantsCount: props.maxInfantsCount }
		: null
	),
	
	...(
		props.defaultValues.adultsCount ?
			{ defaultAdultsCount: props.defaultValues.adultsCount }
			: null
	),
	...(
		props.defaultValues.teensCount ?
			{ defaultTeensCount: props.defaultValues.teensCount }
			: null
	),
	...(
		props.defaultValues.childrenCount ?
			{ defaultChildrenCount: props.defaultValues.childrenCount }
			: null
	),
	...(
		props.defaultValues.infantsCount ?
			{ defaultInfantsCount: props.defaultValues.infantsCount }
			: null
	),
});


const closeOptions = () => {
	dropdownEl?.value.closeOverlay();
};

watch(adults.count, (newVal) => {
	globalBookFlightBus.emit('update-passenger-count', { adults: newVal });
});

watch(children.count, (newVal) => {
	globalBookFlightBus.emit('update-passenger-count', { children: newVal });
});

watch(infants.count, (newVal) => {
	globalBookFlightBus.emit('update-passenger-count', { infants: newVal });
});

watch(cabinClassValue, (newVal) => {
	globalBookFlightBus.emit('update-passenger-count', { cabinClass: newVal.value });
});

const expressBookingInfo = {
	note: props.passengerAndCabinClassNote,
	maxPassengersAdultsCount: 1,
	maxPassengersTeensCount: 0,
	maxPassengersChildrenCount: 0,
	maxPassengersInfantsCount: 0,
};

const isExpressBookingEnabled = computed(() => props.isExpressBooking === true);

const getMaxPassengerCount = (type) => {
	if (isExpressBookingEnabled.value) {
		return expressBookingInfo[`maxPassengers${type}Count`];
	}
	
	const passengerTypeMapping = {
		Adults: adults,
		Teens: teens,
		Children: children,
		Infants: infants,
	}[type];

	return hasReachedMaxPassengersCount.value ? passengerTypeMapping.count.value : unref(passengerTypeMapping.max);
};

const setValue = (key, value) => {
	switch (key) {
		case 'adults': {
			adults.count.value = value;
			break;
		}
		case 'teens': {
			teens.count.value = value;
			break;
		}
		case 'children': {
			children.count.value = value;
			break;
		}
		case 'infants': {
			infants.count.value = value;
			break;
		}
		case 'cabinClass': {
			if (typeof value !== 'object') {
				cabinClassValue.value = computedCabinClassOptions?.value.find((opt) => opt.value === value.toString()) ?? {};
			} else {
				cabinClassValue.value = value;
			}
			
			break;
		}
		default: {
			break;
		}
	}
};
const setValues = (obj) => {
	for (const [key, value] of Object.entries(obj)) {
		setValue(key, value);
	}
};

defineExpose({
	setValue,
	setValues,
	
	cabinClassValue,
	adults,
	teens,
	children,
	infants,
});

</script>

<template>
<div
	class="PassengerAndCabinClass"
	:class="{ 'is-disabled': props.isDisabled, 'is-glass-design': props.enableGlassDesign }"
>
	<Dropdown
		ref="dropdownEl"
		:labelText="props.labelText"
		:ariaLabel="props.labelText"
		desktopVariant="custom-html booking-widget"
		mobileVariant="no-padding custom-html"
		:isClickDropdownMenuToCloseOverlay="!props.isDisabled"
		:enableGlassDesign="props.enableGlassDesign"
		:class="{ 'pointer-events-none': props.isDisabled }"
	>
		<template #mobile-title>
			<slot name="mobile-title"></slot>
		</template>
		<template #placeholder>
			<div class="value-wrapper relative flex-grow mr-2 leading-none" :class="{ 'flex flex-col justify-between': props.enableGlassDesign }">
				<span class="text-label absolute font-semibold">
					{{ props.labelText }}
				</span>

				<span v-if="props.enableGlassDesign" class="text-value flex items-center relative xs:text-sm xs:leading-none">
					{{ totalPassengersIncludedInfantCount }} x <icon-fas-user class="fill-primary-blue-base ml-1 mr-2 w-[13px] h-[14px]" aria-hidden="true" />
					<span v-if="computedCabinClassOptions.length"> </span>
					{{ cabinClassValue.label }}
				</span>
				<span v-else class="text-value relative xs:text-sm xs:leading-none">
					{{ $tc('{n} Passenger', {
						n: totalPassengersIncludedInfantCount,
					}) }}<span v-if="computedCabinClassOptions.length">, </span>
					{{ cabinClassValue.label }}
				</span>

				<!-- <span v-if="props.enableGlassDesign" class="text-count">
					{{ adults.count.value > 0
						? `${adults.count.value} ${adultLabel}`
						: '' }}
					{{ children.count.value > 0
						? `${children.count.value} ${childrenLabel}${children.count.value > 1 ? '' : ''}`
						: '' }}
					{{ infants.count.value > 0
						? `${infants.count.value} ${infantLabel}`
						: '' }}
				</span> -->

			</div>
		</template>
		
		<template #nooptions="{ hideMobileOverlay }">
			<div v-if="!props.enableGlassDesign" class="multiselect-options styled-scrollbar pt-4 cursor-default mr-0 md:mr-2">
				<div class="px-4 typo-category-title mb-4">
					<p class="uppercase">{{ props.passengerLabel }}</p>
				</div>

				<div class="px-4 mb-4">
					<div
						v-if="props.isExpressBooking && !props.hideExpressBookingNote"
						class="bg-primary-blue-extralight text-primary-black-base flex gap-3 justify-start px-4 py-4 rounded-xl"
					>
						<icon-fas-circle-info class="fill-primary-blue-base text-base" />
						<div
							v-html-sanitize="expressBookingInfo.note"
							class="text-sm"
						>
						</div>
					</div>
				</div>
				
				<div class="px-4 flex flex-col gap-4 ">
					<slot name="before-adults"></slot>
					<NumberPlusMinus
						v-model="adults.count.value"
						:name="props.passengerAdultsName"
						:ariaLabel="props.adultLabel"
						:ariaDescription="$t((props.isUKVariant || props.isCountryVariant) ? 'Ages 16 and above' : 'Ages 12 and above')"
						:min="unref(adults.min)"
						:max="getMaxPassengerCount('Adults')"
					>
						<template v-if="$slots['adult-description-label']" #description>
							<slot name="adult-description-label"></slot>
						</template>
					</NumberPlusMinus>
					
					<slot v-if="props.isUKVariant || props.isCountryVariant" name="before-teens"></slot>
					<NumberPlusMinus
						v-if="props.isUKVariant || props.isCountryVariant"
						v-model="teens.count.value"
						:name="props.passengerTeensName"
						:ariaLabel="props.teenagerLabel"
						:ariaDescription="$t('Ages 12 to 15')"
						:min="unref(teens.min)"
						:max="getMaxPassengerCount('Teens')"
					>
						<template v-if="$slots['teen-description-label']" #description>
							<slot name="teen-description-label"></slot>
						</template>
					</NumberPlusMinus>
					<template v-if="props.isShowChildren">
						<slot name="before-children"></slot>
						<NumberPlusMinus
							v-model="children.count.value"
							:name="props.passengerChildrenName"
							:ariaLabel="props.childrenLabel"
							:ariaDescription="$t('Ages 2 to 11')"
							:min="unref(children.min)"
							:max="getMaxPassengerCount('Children')"
						>
							<template v-if="$slots['children-description-label']" #description>
								<slot name="children-description-label"></slot>
							</template>
						</NumberPlusMinus>
					</template>
					<slot name="before-infants"></slot>
					<NumberPlusMinus
						v-model="infants.count.value"
						:name="props.passengerInfantsName"
						:ariaLabel="props.infantLabel"
						:ariaDescription="$t('Ages below 2 for the entire journey')"
						:min="unref(infants.min)"
						:max="unref(infants.max)"
					>
						<template v-if="$slots['infant-description-label']" #description>
							<slot name="infant-description-label"></slot>
						</template>
					</NumberPlusMinus>
				</div>
				
				<div
					v-show="showInfantErrorMsg"
					class="error-msg-container px-4 leading-tight flex text-semantic-red-base text-sm md:flex-col mt-2"
					:role="showInfantErrorMsg ? 'alert' : null"
				>
					<p>{{ $t('Each infant should be accompanied by one adult') }}</p>
				</div>
				
				<div v-if="$slots['after-passengers']" class="px-4 mt-4 mb-4">
					<slot name="after-passengers"></slot>
				</div>
				
				<div v-if="computedCabinClassOptions.length" class="mb-4">
					<hr class="bg-neutral-grey" />
				</div>
				
				<div v-if="computedCabinClassOptions.length" class="px-4 typo-category-title mb-4">
					<p class="uppercase">{{ props.cabinClassLabel }}</p>
				</div>
				
				
				<div v-if="computedCabinClassOptions.length" class="px-4 flex flex-wrap gap-4 mb-6 md:flex-col md:gap-2">
					<slot name="before-cabin-class-options"></slot>
					
					<CTAButton
						v-for="item in computedCabinClassOptions"
						:key="item.value"
						:styleType="cabinClassValue.value === item.value ? 'primary' : 'secondary'"
						:ctaSizing="isMobileViewport ? 'fluid' : 'auto'"
						@click="handleSelectCabinClass(item)"
					>
						<span class="font-semibold">{{ item.label }}</span>
					</CTAButton>
					
					<div class="sr-only" role="alert">
						{{ $t('Currently selected cabin class is {selectedCabinLabel}', { selectedCabinLabel: cabinClassValue.label }) }}
					</div>
				</div>
			</div>
			<div v-else class="multiselect-options styled-scrollbar pt-4 cursor-default mr-0 md:mr-2 flex flex-col">
				<div
					class="flex flex-row md:flex-col"
				>
					<div>
						<div class="px-4 typo-category-title mb-4">
							<p>{{ props.passengerLabel }}</p>
						</div>

						<div class="px-4 mb-4">
							<div
								v-if="props.isExpressBooking && !props.hideExpressBookingNote"
								class="bg-primary-blue-extralight text-primary-black-base flex gap-3 justify-start px-4 py-4 rounded-xl"
							>
								<icon-fas-circle-info class="fill-primary-blue-base text-base" />
								<div
									v-html-sanitize="expressBookingInfo.note"
									class="text-sm"
								>
								</div>
							</div>
						</div>
						
						<div class="px-4 mb-4 flex flex-col gap-4 ">
							<slot name="before-adults"></slot>
							<NumberPlusMinus
								v-model="adults.count.value"
								:name="props.passengerAdultsName"
								:ariaLabel="props.adultLabel"
								:ariaDescription="$t((props.isUKVariant || props.isCountryVariant) ? 'Ages 16 and above' : 'Ages 12 and above')"
								:min="unref(adults.min)"
								:max="getMaxPassengerCount('Adults')"
							>
								<template v-if="$slots['adult-description-label']" #description>
									<slot name="adult-description-label"></slot>
								</template>
							</NumberPlusMinus>
							
							<slot v-if="props.isUKVariant || props.isCountryVariant" name="before-teens"></slot>
							<NumberPlusMinus
								v-if="props.isUKVariant || props.isCountryVariant"
								v-model="teens.count.value"
								:name="props.passengerTeensName"
								:ariaLabel="props.teenagerLabel"
								:ariaDescription="$t('Ages 12 to 15')"
								:min="unref(teens.min)"
								:max="getMaxPassengerCount('Teens')"
							>
								<template v-if="$slots['teen-description-label']" #description>
									<slot name="teen-description-label"></slot>
								</template>
							</NumberPlusMinus>
							<template v-if="props.isShowChildren">
								<slot name="before-children"></slot>
								<NumberPlusMinus
									v-model="children.count.value"
									:name="props.passengerChildrenName"
									:ariaLabel="props.childrenLabel"
									:ariaDescription="$t('Ages 2 to 11')"
									:min="unref(children.min)"
									:max="getMaxPassengerCount('Children')"
								>
									<template v-if="$slots['children-description-label']" #description>
										<slot name="children-description-label"></slot>
									</template>
								</NumberPlusMinus>
							</template>
							<slot name="before-infants"></slot>
							<NumberPlusMinus
								v-model="infants.count.value"
								:name="props.passengerInfantsName"
								:ariaLabel="props.infantLabel"
								:ariaDescription="$t('Ages below 2 for the entire journey')"
								:min="unref(infants.min)"
								:max="unref(infants.max)"
							>
								<template v-if="$slots['infant-description-label']" #description>
									<slot name="infant-description-label"></slot>
								</template>
							</NumberPlusMinus>
						</div>
						
						<div
							v-show="showInfantErrorMsg"
							class="error-msg-container px-4 leading-tight flex text-semantic-red-base text-sm md:flex-col mt-2"
							:role="showInfantErrorMsg ? 'alert' : null"
						>
							<p>{{ $t('Each infant should be accompanied by one adult') }}</p>
						</div>
						
						<div v-if="$slots['after-passengers']" class="px-4 mt-8 mb-4">
							<slot name="after-passengers"></slot>
						</div>
					</div>
					<div class="relative cabin-class-wrapper md:w-1/1 w-1/2">
						<div>
							<div v-if="computedCabinClassOptions.length" class="px-4 typo-category-title">
								<p class="uppercase">{{ props.cabinClassLabel }}</p>
							</div>
							
							
							<div v-if="computedCabinClassOptions.length" class="px-4 flex flex-col flex-wrap gap-4 md:flex-col md:gap-2">
								<slot name="before-cabin-class-options"></slot>
								<div
									v-for="item in computedCabinClassOptions"
									:key="item.value"
									class="flex justify-between items-center w-full h-[55px]"
								>
									<span>{{ item.label }}</span>
									<button
										type="button"
										class="icon-wrapper text-neutral-white-base"
										:class="cabinClassValue.value === item.value ? 'bg-primary-blue-base' : 'bg-neutral-grey-dark'"
										@click="handleSelectCabinClass(item)"
									>
										<img :src="item.icon" class="h-6 w-6" />
									</button>
								</div>
								
								<div class="sr-only" role="alert">
									{{ $t('Currently selected cabin class is {selectedCabinLabel}', { selectedCabinLabel: cabinClassValue.label }) }}
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="cta-wrapper w-full md:mb-2">
					<CTAButton class="md:w-[80%] md:mx-auto" @click.stop="isMobileViewport ? hideMobileOverlay() : closeOptions()">
						{{ $t('Done') }}
					</CTAButton>
				</div>
			</div>

			<div v-if="!props.enableGlassDesign" class="sticky bottom-0 bg-white py-4 px-4 border-t-2 border-neutral-grey-base >md:hidden">
				<CTAButton
					alignmentMobile="right"
					@click="hideMobileOverlay"
				>{{ $t('Done') }}</CTAButton>
			</div>
		</template>
	</Dropdown>
	
	<TextField
		variant="input-hidden"
		:name="props.cabinClassName"
		:ariaLabel="$t('Selected cabin class')"
		:modelValue="cabinClassValue.value"
	/>
</div>
</template>


<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.PassengerAndCabinClass {
	@media #{var.$query-min-md} {
	:deep(.multiselect-options) {
   overscroll-behavior: unset !important;
 }
}

	&::v-deep(.Dropdown[data-use-theme="MH"]) {
		[data-variant*="booking-widget"] {
			.multiselect-dropdown {
				min-width: 400px;
			}
		}
	}

	&::v-deep(.Dropdown[data-use-theme="amal"]) {
		[data-variant*="booking-widget"] {
			.multiselect-dropdown {
				min-width: 400px;
			}
		}
	}
	
	&.is-disabled {
		pointer-events: none;

		&::v-deep(.Dropdown) {
			.text-label,
			.text-value {
				color: var(--neutral-grey-dark) !important;
			}

			.dropdown-caret {
				fill: var(--neutral-grey-dark);
			}
		}
	}

	&.is-glass-design {

		:deep(.Dropdown){
			.text-label {
				font-size: 10px;
			}

			.label-text {
				font-size: 16px;
			}

			.value-wrapper {
				padding-top: 0rem;
				
				.text-value {
					top: 0.32rem;
				}
			}
		}

		:deep(.multiselect-main) {

			.text-label {
				font-weight: 700;
				font-style: Bold;
				font-size: 16px;
				line-height: 15px;
				letter-spacing: 0.62px;
				color: #99A1AF;
				display: block !important;
				transform: translate(0, -0.65rem) !important;
			}

			.text-value {
				font-weight: 700 !important;
				font-size: 16px;
				line-height: 24px;
				letter-spacing: -0.31px;
				color: #101828 !important;
			}

			.text-count {
				font-weight: 400;
				font-style: Regular;
				font-size: 12px;
				line-height: 16px;
				letter-spacing: 0px;
				color: #6A7282;
			}

			input {
				margin-top: 6px;
			}
		}

		:deep(.multiselect-dropdown) {
			width: 858px;
			transform: translate(-30%, calc(100% + 0.37rem));

			@media #{var.$query-max-lg} {
				width: 100% !important;
				transform: translate(0, calc(100% + 0.37rem));
				
			}
		}

		:deep(.cabin-class-wrapper) {
			border-left: 1px solid var(--neutral-grey-base);
		}

		:deep(.icon-wrapper) {
			border-radius: 8px;
			width: 44px !important;
			height: 40px;
			display: flex;
			justify-content: center;
			align-items: center;
		}

		:deep(.cta-wrapper) {
			border-top: 2px solid #EEEEEE;
			height: 69px;
			padding-top: 8px;
			padding-right: 24px;
			padding-bottom: 8px;
			padding-left: 24px;
			display: flex;
			justify-content: flex-end;
		}

		@media #{var.$query-max-md} {

			:deep(.promo-code-wrapper) {
				height: 77px;
				opacity: 1;
				gap: 10px;
				border-radius: 100px;
				border-width: 1px;
				border: 1px solid;
				border: 1px solid #EEEEEE;
				padding: 8px 8px;
				margin-left: 8px;
				margin-right: 8px;
				margin-bottom: 10px;

				:deep(.text-label) {
					display: block;
				}
			}
		}

	}

	:deep(.glass-design-variant) {

		.text-label {
			display: block !important;
		}
	}
}

</style>